import { useState } from 'react';
import axios from 'axios';
import { X, Eye, PaperPlaneTilt } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/native-datetime';
import { toast } from 'sonner';
import { formatSubmitError } from '@/utils/formErrors';
import { useNavigate } from 'react-router-dom';
import { EMPLOYEE_NAMES as FALLBACK_NAMES } from '@/lib/workCalculations';
import useDirectory from '@/hooks/useDirectory';
import useUsedDates from '@/hooks/useUsedDates';
import { Req, labelCls, fieldInput, fieldTextarea, fieldTrigger, btnPrimary, btnSecondary } from '@/components/forms/formKit';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = {
  form_type: 'leave_transfer',
  employee_name: '',
  employee_code: '',
  id_card: '',
  department: '',
  holiday_date: '',
  work_date: '',
  reason: '',
};

export default function LeaveTransferFormModal({ onClose, initialData, submissionId, embedded = false }) {
  const navigate = useNavigate();
  const { employees, employeeNames, departmentNames } = useDirectory();
  const empOptions = employeeNames.length ? employeeNames : FALLBACK_NAMES;
  const [showPreview, setShowPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(() => ({ ...EMPTY, ...(initialData || {}) }));
  const isEdit = !!submissionId;
  const usedDates = useUsedDates('leave_transfer', [formData.employee_name], submissionId);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'employee_name') {
        const emp = employees.find((e) => e.name === value);
        updated.employee_code = emp?.code || '';
        updated.id_card = emp?.id_card || '';
        if (emp && emp.department_name && !prev.department) updated.department = emp.department_name;
      }
      if (field === 'department') {
        const emp = employees.find((e) => e.name === prev.employee_name);
        if (!emp || emp.department_name !== value) updated.employee_name = '';
      }
      return updated;
    });
  };

  const filteredEmpOptions = formData.department
    ? employees.filter((e) => e.department_name === formData.department).map((e) => e.name)
    : empOptions;

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      if (isEdit) {
        await axios.put(`${API}/forms/submissions/${submissionId}`, { form_data: formData });
        toast.success('申請を更新しました');
        if (onClose) onClose();
        setTimeout(() => navigate(`/submission/${submissionId}`), 300);
      } else {
        await axios.post(`${API}/forms/submit`, formData);
        toast.success('登録しました — 履歴に追加されました');
        setFormData({ ...EMPTY });
        setShowPreview(false);
      }
    } catch (error) {
      console.error('Submit failed:', error);
      toast.error(formatSubmitError(error, isEdit));
    } finally {
      setSubmitting(false);
    }
  };

  const canPreview = formData.employee_name && formData.department && 
    formData.holiday_date && formData.work_date && formData.reason;

  const body = (
    <>
      <div className="sticky top-0 bg-white border-b border-zinc-100 px-6 py-5 flex items-center justify-between z-20">
        <h2 className="text-2xl font-bold tracking-tight text-zinc-900">
          振替休暇申請書{isEdit && <span className="ml-2 text-sm font-normal text-zinc-500">— 編集</span>}
        </h2>
        {onClose && (
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-full transition-colors text-zinc-500 hover:text-zinc-900">
            <X size={24} />
          </button>
        )}
      </div>

        {!showPreview ? (
          <div className="p-6 sm:p-8 space-y-6">
            <div>
              <Label htmlFor="department" className={labelCls}>所属部署<Req /></Label>
              {departmentNames.length > 0 ? (
                <Select value={formData.department} onValueChange={(val) => handleChange('department', val)}>
                  <SelectTrigger data-testid="select-department" className={`mt-1.5 ${fieldTrigger}`}>
                    <SelectValue placeholder="所属部署を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    {departmentNames.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="department"
                  value={formData.department}
                  onChange={(e) => handleChange('department', e.target.value)}
                  className={`mt-1.5 ${fieldInput}`}
                  required
                />
              )}
            </div>
            <div>
              <Label className={labelCls}>氏名<Req /></Label>
              <Select
                value={formData.employee_name}
                onValueChange={(val) => handleChange('employee_name', val)}
                disabled={!formData.department && departmentNames.length > 0}
              >
                <SelectTrigger data-testid="select-employee-name" className={`mt-1.5 ${fieldTrigger} disabled:opacity-50`}>
                  <SelectValue placeholder={formData.department ? '氏名を選択してください' : 'まず所属部署を選択してください'} />
                </SelectTrigger>
                <SelectContent>
                  {filteredEmpOptions.map((name) => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.department && filteredEmpOptions.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">この部署に登録された社員がいません。</p>
              )}
            </div>
            <div>
              <Label htmlFor="holiday_date" className={labelCls}>振替元（休日）<Req /></Label>
              <DatePicker
                id="holiday_date"
                testId="input-holiday-date"
                value={formData.holiday_date}
                onChange={(v) => handleChange('holiday_date', v)}
                disabledDates={usedDates}
                warnNonWorking
                required
              />
              <p className="text-xs text-zinc-500 mt-1">代休を取得する休日（土日・祝日・会社休業日）を選択してください</p>
            </div>
            <div>
              <Label htmlFor="work_date" className={labelCls}>振替先（出勤日）<Req /></Label>
              <DatePicker
                id="work_date"
                testId="input-work-date"
                value={formData.work_date}
                onChange={(v) => handleChange('work_date', v)}
                disabledDates={usedDates}
                warnNonWorking
                required
              />
              <p className="text-xs text-zinc-500 mt-1">実際に出勤した日を選択してください（休日出勤の場合も選択できます）</p>
            </div>
            <div>
              <Label htmlFor="reason" className={labelCls}>振替理由<Req /></Label>
              <Textarea
                id="reason"
                value={formData.reason}
                onChange={(e) => handleChange('reason', e.target.value)}
                className={`mt-1.5 ${fieldTextarea}`}
                placeholder="振替えが必要な理由を記載してください"
                required
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={onClose} className={btnSecondary}>
                キャンセル
              </Button>
              <Button
                onClick={() => setShowPreview(true)}
                disabled={!canPreview}
                className={btnPrimary}
              >
                <Eye size={18} className="mr-2" />
                プレビュー
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="bg-gradient-to-br from-zinc-50 to-white rounded-lg border-2 border-dashed border-zinc-200 p-6 sm:p-8 mb-6 shadow-inner">
              <h3 className="text-lg font-bold mb-4 border-b border-zinc-300 pb-2">申請内容の確認</h3>
              <dl className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-zinc-600">氏名:</dt>
                  <dd className="col-span-2 text-zinc-900"><span data-no-translate>{formData.employee_name}</span></dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-zinc-600">所属部署:</dt>
                  <dd className="col-span-2 text-zinc-900">{formData.department}</dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-zinc-600">振替元（休日）:</dt>
                  <dd className="col-span-2 text-zinc-900">{formData.holiday_date}</dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-zinc-600">振替先（出勤日）:</dt>
                  <dd className="col-span-2 text-zinc-900">{formData.work_date}</dd>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <dt className="font-medium text-zinc-600">振替理由:</dt>
                  <dd className="col-span-2 text-zinc-900 whitespace-pre-wrap">{formData.reason}</dd>
                </div>
              </dl>
            </div>
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowPreview(false)} className="border-zinc-300">
                戻る
              </Button>
              <Button onClick={handleSubmit} disabled={submitting} data-testid="form-submit-btn" className="bg-teal-700 hover:bg-teal-800 shadow-sm hover:shadow-md transition-all">
                <PaperPlaneTilt size={18} className="mr-2" />
                {submitting ? (isEdit ? '更新中…' : '登録中…') : (isEdit ? '更新する' : '登録する')}
              </Button>
            </div>
          </div>
        )}
    </>
  );

  if (embedded) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-zinc-200 w-full overflow-hidden" data-testid="leave-transfer-edit">
        {body}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-zinc-900/70 pointer-events-auto">
      <div className="bg-white rounded-lg shadow-xl border border-zinc-200 w-full max-w-2xl max-h-[calc(100vh-7rem)] overflow-y-auto mt-20 sm:mt-24 mx-4 mb-4">
        {body}
      </div>
    </div>
  );
}
